import { DateTime } from "luxon";
import { cloneDeep } from "lodash-es";

import LocalForagePlugin, { categories, syncState } from "@/plugins/LocalForagePlugin";
import { categoriesSyncKey } from "@/types/products/product-definitions";

import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@/types/api/fetch";

import type { Categories } from "@/types/api/content-models";
import type { Category } from "@/types/entities";

import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";
const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export async function refreshCategoriesFromServer(): Promise< Category[] > {
    let allCategories: Category[] = [];
    // Timing - testing to see how expensive each method is
    const t0: number = performance.now();

    let page: number = 0;
    const pageSize: number = 500;

    let categoriesPayload: Category[] | undefined = [];

    do {
        const response =
            await goFetch< Categories, PersistedQueryPayload >( {
                endpoint: 'content/graphql',
                payload: {
                    id: '1406515c5acd2606068ab34969384860',
                    variables: {
                        skip: page,
                        limit: pageSize
                    }
                }
            } );

        if ( response?.data?.categoryCatalog?.categories ) {
            categoriesPayload = [ ...( response.data.categoryCatalog.categories
                    // Perform a map to remove the extraneously added meta data from Apollo
                    .map( ( c: Category ): Category => ( {
                            id: c.id,
                            name: c.name,
                            slug: c.slug
                        } )
                    )
            ) ];

            const t1: number = performance.now();
            logger.info( `Fetching & mapping ${ categoriesPayload.length } category items took ${ t1 - t0 } ms` );

            allCategories = [ ...categoriesPayload, ...allCategories ];
        }

        // update the page #
        page++;
    } while ( (categoriesPayload?.length ?? 0) >= pageSize );

    return allCategories;
}

export async function loadCategoryMap( categoriesMap: Map< string, Category > ): Promise< boolean > {
    let updated = false;
    try {
        const t0: number = performance.now();

        if ( typeof categories === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem< number >( categoriesSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
            
            const nextSyncThresholdInMillis = DateTime.utc().plus( { hours: 4 } ).toMillis();
            await syncState.setItem( categoriesSyncKey, nextSyncThresholdInMillis );

            // Retrieve the latest collection of categories from the content service
            const categoriesReceived: Category[] = await refreshCategoriesFromServer();

            if(categoriesReceived.length > 0)
            {
                updated = true;
                // Store the items recently retrieved
                await categories.ready();
                for ( const category of categoriesReceived ) {
                    await categories.setItem( category.id, cloneDeep( category ) );
                }

                const t1: number = performance.now();
                logger.info( `[ sync-worker ] Reset cache for ${ categoriesMap.size } categories in ${ t1 - t0 } ms` );
            }
            else
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( categoriesSyncKey, nextSyncThresholdInMillis );
            }
        }

        // Make sure map is empty
        categoriesMap.clear();

        // Load whatever is in the local cache
        await categories.ready();
        // Iterate over all elements within IDB
        await categories.iterate( ( category: Category ) => {
            categoriesMap.set( category.id, category );
            return;
        } );
    } catch ( err: any ) {
        logger.error( JSON.stringify( err.message ) );
    }

    return updated;
}
